import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { galleryProjects } from '../../mocks/homeData';

export default function GallerySection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(5, Math.min(95, x)));
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.touches[0].clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(5, Math.min(95, x)));
  };

  const project = galleryProjects[0];

  return (
    <section id="gallery" ref={sectionRef} className="bg-white py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div
            className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="font-body font-semibold text-teal text-xs uppercase tracking-widest">Our Work</span>
            <h2 className="font-heading font-bold text-charcoal text-2xl md:text-3xl lg:text-4xl mt-2">
              Real Transformations
            </h2>
            <p className="font-body text-charcoal/60 text-sm md:text-base mt-3 max-w-lg mx-auto">
              Drag the slider to see the before and after. Real projects completed for South Florida homeowners.
            </p>
          </div>

          <div
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div
              ref={containerRef}
              className="relative w-full h-[340px] md:h-[460px] lg:h-[540px] rounded-lg overflow-hidden cursor-ew-resize select-none"
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              onTouchMove={handleTouchMove}
              onTouchStart={() => setIsDragging(true)}
              onTouchEnd={() => setIsDragging(false)}
            >
              {/* After image */}
              <img
                src={project.afterImage}
                alt={`${project.title} after`}
                className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
                draggable={false}
              />
              <div className="absolute top-4 right-4 bg-black/60 px-3 py-1 rounded text-white font-body text-xs uppercase tracking-wider z-10">
                After
              </div>

              {/* Before image */}
              <div
                className="absolute inset-y-0 left-0 overflow-hidden"
                style={{ width: `${sliderPosition}%` }}
              >
                <img
                  src={project.beforeImage}
                  alt={`${project.title} before`}
                  className="absolute inset-0 h-full object-cover object-top pointer-events-none"
                  style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : '100vw', maxWidth: 'none' }}
                  draggable={false}
                />
                <div className="absolute top-4 left-4 bg-black/60 px-3 py-1 rounded text-white font-body text-xs uppercase tracking-wider">
                  Before
                </div>
              </div>

              {/* Slider handle */}
              <div
                className="absolute top-0 bottom-0 w-1 bg-teal cursor-ew-resize z-20"
                style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-teal flex items-center justify-center shadow-lg">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-left-right-line text-white text-sm"></i>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold text-charcoal text-base md:text-lg">{project.title}</h3>
                <div className="flex flex-wrap items-center gap-3 mt-1.5">
                  <span className="font-body text-charcoal/55 text-xs flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-map-pin-line text-teal text-xs"></i>
                    </div>
                    {project.location}
                  </span>
                  <span className="font-body text-charcoal/55 text-xs flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line text-teal text-xs"></i>
                    </div>
                    {project.duration}
                  </span>
                  <span className="font-body text-charcoal/55 text-xs">{project.type}</span>
                </div>
              </div>
              <button
                onClick={() => navigate('/gallery')}
                className="whitespace-nowrap border border-teal text-teal hover:bg-teal hover:text-white font-body text-sm px-5 py-2.5 rounded-lg transition-all flex items-center gap-2 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-gallery-line"></i>
                </div>
                View Full Portfolio
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}